import React from "react";
import { Link } from "react-router-dom";
import {
  Handshake,
  Shield,
  Database,
  Scale,
  Mail,
  Download,
  Check,
  Building2,
} from "lucide-react";
import SEO from "../components/SEO";

// Outreach letter the user (app owner) can send to potential partners.
// Plain text on purpose — any org's intake lawyer can paste directly into
// a formal MOU without re-formatting.
const OUTREACH_TEMPLATE = `Subject: Data-sharing partnership — Paws on Record national registry

Dear [Recipient name / Partnerships team],

My name is [Your name] and I operate Paws on Record
(https://pawsonrecord.com), a free public registry that aggregates
convicted animal abusers across all 50 U.S. states. Shelters, rescues,
and fostering families use it to run a quick background check before
placing an animal.

Today, our registry is populated from:
  • The Tennessee Bureau of Investigation statewide registry (pulled in full)
  • DOJ USAO + ENRD press releases (federal PACT Act / AWA / Lacey Act)
  • LA County District Attorney announcements
  • Publicly reported news convictions (ALDF cases-of-the-month, etc.)
  • A community tip form with human review

Current registry size: ~205 named, sourced conviction records across 50 states.

The hard truth: most state / county animal-abuser registries are
deliberately designed to block bulk-scraping (reCAPTCHA, search-only
forms, paywalled court portals). The long tail of U.S. cruelty
convictions — estimated at 10,000–30,000 per year — is effectively
invisible to the public.

We think your organization has access to a meaningful slice of that
data, and we'd like to explore a formal data-sharing agreement.

What we can offer:
  1. Full attribution on every record — each one carries a "Data-sharing
     partner · [Your Org]" source label visible to the public.
  2. API-level access to the consolidated registry for your own staff.
  3. Anonymous aggregate analytics (no PII) — e.g., per-state conviction
     trends — sent quarterly for your advocacy reporting.
  4. A published MOU / agreement reference on our partners page so your
     contribution is a matter of public record.

What we'd ask from you:
  1. A one-time or rolling export of named-defendant conviction records
     (CSV or JSON) — defendant name, city, state, crime, date, source.
  2. A point-of-contact for ongoing updates and correction requests.
  3. Review and approval of any text describing the partnership on our
     About and Partnerships pages.

Data provenance, legal risk allocation, and takedown logic are already
documented at /about and /legal. I'd welcome a 30-minute call to walk
through specifics and share our existing schema.

Thank you for protecting animals.

[Your name]
[Your email]
[Your phone]
Paws on Record`;

const TARGET_ORGS = [
  {
    name: "ASPCA",
    focus: "National cruelty investigations, NYC + multi-state ops",
    url: "https://www.aspca.org/about-us/contact-us",
  },
  {
    name: "Animal Legal Defense Fund (ALDF)",
    focus: "Nationwide case law database; state rankings; prosecutorial support",
    url: "https://aldf.org/contact/",
  },
  {
    name: "The Humane Society of the United States (HSUS)",
    focus: "State legislative work + cruelty campaigns in all 50 states",
    url: "https://www.humanesociety.org/contact",
  },
  {
    name: "National Sheriffs' Association — Animal Cruelty Task Force",
    focus: "Officer training + arrest/conviction tracking",
    url: "https://www.sheriffs.org/",
  },
  {
    name: "National Animal Care & Control Association (NACA)",
    focus: "Local animal-control agency network; intake + case outcomes",
    url: "https://www.nacanet.org/contact/",
  },
  {
    name: "Tennessee Bureau of Investigation (already integrated)",
    focus: "Statewide animal-abuse registry — full data pulled Feb 2026",
    url: "https://www.tn.gov/tbi/general-information/tennessee-animal-abuse-registry.html",
    active: true,
  },
  {
    name: "Cook County Sheriff IL (target)",
    focus: "Statutory registry — 100–300 estimated records",
    url: "https://cookcountysheriffil.gov/animal-abuser-registry/",
  },
  {
    name: "Suffolk County NY SPCA (target)",
    focus: "Statutory registry — estimated 80–150 records",
    url: "https://www.suffolkcountyspca.org/",
  },
];

export default function Partnerships() {
  const downloadTemplate = () => {
    const blob = new Blob([OUTREACH_TEMPLATE], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "paws-on-record-outreach-letter.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12" data-testid="partnerships-page">
      <SEO
        title="Partnerships · Data Sharing for Shelters, Rescues & AGs"
        description="Partnership program for shelters, rescues, prosecutors, and state Attorneys General. Bulk record export, API access, and data-sharing agreements to close the animal-cruelty visibility gap."
        path="/partnerships"
      />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="label-eyebrow">Section · 05 · Partnerships</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2">
            205 records isn't
            <br />
            the real number.
          </h1>
          <p className="text-lg text-zinc-700 mt-6 leading-relaxed">
            There are an estimated <strong>10,000–30,000 felony
            animal-cruelty convictions in the U.S. every year</strong>, plus
            hundreds of thousands of misdemeanor pleas. Most are invisible to
            the public — tucked inside county court portals, search-only
            registries, or paywalled federal PACER records.
          </p>
          <p className="text-lg text-zinc-700 mt-4 leading-relaxed">
            Closing that gap requires <strong>data-sharing agreements</strong>{" "}
            with the organizations that already hold the data: national
            welfare groups, state animal-control networks, and the handful of
            sheriff's offices that maintain statutory registries.
          </p>
          <p className="text-base text-zinc-600 mt-4 leading-relaxed">
            This page exists so those organizations can see who we are, what
            we offer, and how to open a conversation.
          </p>
        </div>
        <div className="lg:col-span-5">
          <div className="border border-zinc-900 bg-zinc-900 text-white p-6" data-testid="contact-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 border border-red-600 bg-red-600 flex items-center justify-center">
                <Handshake size={18} />
              </div>
              <div className="label-eyebrow text-red-500">Start the conversation</div>
            </div>
            <div className="font-display font-black text-2xl mt-4 leading-tight">
              Partnership inquiries
            </div>
            <p className="text-sm text-zinc-300 mt-3 leading-relaxed">
              Download the pre-written outreach letter, fill in your details,
              and send it to any of the target orgs below. Or share this
              page's URL directly with their partnerships team.
            </p>
            <button
              onClick={downloadTemplate}
              className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 border border-white bg-white text-zinc-900 text-[11px] uppercase tracking-widest font-bold hover:bg-red-600 hover:border-red-600 hover:text-white transition-colors"
              data-testid="download-outreach-btn"
            >
              <Download size={14} /> Download outreach letter (.txt)
            </button>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/partnerships/pitch.pdf`}
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex items-center gap-2 px-4 py-2.5 border border-red-600 bg-red-600 text-white text-[11px] uppercase tracking-widest font-bold hover:bg-white hover:text-zinc-900 hover:border-white transition-colors"
              data-testid="download-pitch-pdf-btn"
            >
              <Download size={14} /> Download PDF pitch (1-pager)
            </a>
            <div className="mt-5 pt-5 border-t border-zinc-700">
              <div className="label-eyebrow text-red-500">Direct contact</div>
              <a
                href="mailto:info@pawsonrecord.com"
                className="block mt-2 font-mono text-sm hover:text-red-500 transition-colors"
                data-testid="partnership-email"
              >
                info@pawsonrecord.com
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-5">
        <Value icon={<Shield size={18} />} title="Full attribution" body="Every partner-sourced record is tagged with a 'Data-sharing partner · [Your Org]' label visible to the public." />
        <Value icon={<Database size={18} />} title="API + analytics access" body="Partners receive API-level access plus anonymized quarterly aggregate analytics for advocacy reporting." />
        <Value icon={<Scale size={18} />} title="MOU + takedown logic" body="Formal written agreement; existing takedown workflow protects partners from legal exposure when a record is challenged." />
      </div>

      <div className="mt-16">
        <div className="label-eyebrow flex items-center gap-2">
          <Building2 size={12} /> Target organizations
        </div>
        <h2 className="font-display font-black text-3xl tracking-tight mt-2">
          Who we're already talking to — and who we'd like to.
        </h2>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="partner-org-grid">
        {TARGET_ORGS.map((o) => (
          <a
            key={o.name}
            href={o.url}
            target="_blank"
            rel="noreferrer"
            className={`block border p-5 hover-shadow-harsh ${o.active ? "border-emerald-600 bg-emerald-50" : "border-zinc-300 bg-white"}`}
            data-testid={`partner-org-${o.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`}
          >
            <div className="flex items-start gap-3">
              <div className={`w-9 h-9 shrink-0 flex items-center justify-center ${o.active ? "bg-emerald-600 text-white" : "bg-zinc-900 text-white"}`}>
                {o.active ? <Check size={16} /> : <Mail size={16} />}
              </div>
              <div>
                <div className="font-display font-black text-lg tracking-tight leading-tight">
                  {o.name}
                </div>
                <div className="text-sm text-zinc-700 mt-1 leading-relaxed">
                  {o.focus}
                </div>
                {o.active && (
                  <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 mt-2">
                    · Active partner
                  </div>
                )}
              </div>
            </div>
          </a>
        ))}
      </div>

      <div className="mt-16 border border-zinc-900 bg-white p-8">
        <div className="label-eyebrow">How partner data flows in</div>
        <h3 className="font-display font-black text-2xl tracking-tight mt-2">
          One endpoint. Full attribution. Audit trail.
        </h3>
        <ol className="mt-4 space-y-2 text-zinc-700 text-sm leading-relaxed list-decimal list-inside">
          <li>Partner signs MOU + sends CSV or JSON export of named-defendant convictions.</li>
          <li>Paws on Record admin posts the batch to <code className="font-mono text-xs bg-zinc-100 px-1.5 py-0.5 border border-zinc-300">POST /api/admin/partners/import</code>.</li>
          <li>Each record is upserted with <code className="font-mono text-xs">partner_org</code>, <code className="font-mono text-xs">partner_agreement_ref</code>, and an immutable <code className="font-mono text-xs">partner_imported_at</code> timestamp.</li>
          <li>Public registry surfaces the partner name as the source label. Takedown / correction requests route back to the partner's point of contact.</li>
          <li>Quarterly aggregate analytics (counts, states, crime types — zero PII) shipped back to the partner.</li>
        </ol>
        <div className="mt-5 text-sm text-zinc-600">
          <Link to="/legal" className="underline">Full legal framework →</Link>
        </div>
      </div>
    </div>
  );
}

function Value({ icon, title, body }) {
  return (
    <div className="border border-zinc-300 bg-white p-5">
      <div className="w-9 h-9 bg-zinc-900 text-white flex items-center justify-center">
        {icon}
      </div>
      <div className="font-display font-black text-lg tracking-tight mt-3">{title}</div>
      <p className="text-sm text-zinc-700 mt-2 leading-relaxed">{body}</p>
    </div>
  );
}
